import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Search, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

function AddStockForm({ customerId, onSubmit, loading = false, globalProducts, categories = [] }) {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      amount: 0,
      method: 'stok_girisi',
      note: ''
    }
  });

  // Auto-set amount to 0 for stock entries (no payment)
  useEffect(() => {
    setValue('amount', 0);
  }, [setValue]);

  // Helper function to safely get product name
  const getProductName = (product) => {
    return product?.name || product?.productName || 'Bilinmeyen Ürün';
  };

  // Helper function to get category name
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'Kategori Yok';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Kategori Yok';
  };

  // Group products by category
  const groupProductsByCategory = (products) => {
    const grouped = {};
    
    products.forEach(product => {
      const categoryId = product.category_id || 'no-category';
      const categoryName = getCategoryName(product.category_id);
      
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          id: categoryId,
          name: categoryName,
          products: []
        };
      }
      grouped[categoryId].products.push(product);
    });

    // Sort categories (put "Kategori Yok" at the end)
    const sortedCategories = Object.values(grouped).sort((a, b) => {
      if (a.id === 'no-category') return 1;
      if (b.id === 'no-category') return -1;
      return a.name.localeCompare(b.name, 'tr');
    });

    return sortedCategories;
  };

  // Toggle category collapse
  const toggleCategoryCollapse = (categoryId) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(categoryId)) {
      newCollapsed.delete(categoryId);
    } else {
      newCollapsed.add(categoryId);
    }
    setCollapsedCategories(newCollapsed);
  };

  // Watch method field
  const method = watch('method');

  // Remove product from selection
  const removeProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  // Add product to selection
  const addProduct = (product) => {
    const existingIndex = selectedProducts.findIndex(p => p.id === product.id);
    
    if (existingIndex >= 0) {
      // Update existing product quantity
      const newProducts = [...selectedProducts];
      newProducts[existingIndex].quantity = (newProducts[existingIndex].quantity || 0) + 1;
      setSelectedProducts(newProducts);
    } else {
      // Add new product
      setSelectedProducts(prev => [...prev, {
        id: product.id,
        name: getProductName(product),
        unit: product.unit || 'adet',
        quantity: 1
      }]);
    }
  };

  // Update product quantity
  const updateProductQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeProduct(productId);
      return;
    }
    
    setSelectedProducts(prev => 
      prev.map(p => 
        p.id === productId 
          ? { ...p, quantity: parseFloat(newQuantity) } 
          : p
      )
    );
  };

  // Sort and filter products for search
  const availableProducts = globalProducts ? globalProducts.sort((a, b) => 
    getProductName(a).localeCompare(getProductName(b), 'tr')
  ) : [];
  
  const filteredProducts = availableProducts.filter(product =>
    getProductName(product).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const onFormSubmit = async (data) => {
    // Validate that at least one product is selected
    if (selectedProducts.length === 0) {
      toast.error('❌ En az bir ürün seçmelisiniz!', {
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    // Validate quantities
    const hasInvalidQuantity = selectedProducts.some(p => !p.quantity || p.quantity <= 0);
    if (hasInvalidQuantity) {
      toast.error('❌ Tüm ürünler için geçerli miktar giriniz!', {
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    try {
      await onSubmit({
        amount: parseFloat(data.amount) || 0,
        method: data.method,
        note: data.note,
        products: selectedProducts
      });
      
      // Reset form on success
      setSelectedProducts([]);
      setSearchTerm('');
      setValue('amount', 0);
      setValue('note', '');
      
      toast.success('✅ Stok girişi başarıyla kaydedildi!', {
        duration: 4000,
        position: 'top-right',
      });
      
    } catch (error) {
      console.error('Stok form error:', error);
      // Error handling is done in parent component
    }
  };

  if (!globalProducts || globalProducts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 px-4 bg-gray-50 rounded-apple border-2 border-dashed border-gray-200">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ürün Kataloğu Yüklenemedi</h3>
          <p className="text-sm text-gray-600 mb-4">
            Ürün kataloğu yüklenirken bir hata oluştu.
          </p>
          <p className="text-xs text-gray-500">
            Lütfen sayfayı yenileyin veya admin ile iletişime geçin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
        {/* Payment Amount Field - Hidden for stock entries */}
        {method !== 'stok_girisi' && (
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
              Tutar (₺) *
            </label>
            <input
              id="amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              className={`input-field ${errors.amount ? 'border-danger-500 focus:ring-danger-500' : ''}`}
              {...register('amount', {
                required: method !== 'stok_girisi' ? 'Tutar gereklidir' : false,
                min: { value: 0, message: 'Tutar 0\'dan büyük olmalıdır' }
              })}
            />
            {errors.amount && (
              <p className="text-sm text-danger-600 mt-1">{errors.amount.message}</p>
            )}
          </div>
        )}

        {/* Payment Method Field */}
        <div>
          <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
            İşlem Türü *
          </label>
          <select
            id="method"
            className={`input-field ${errors.method ? 'border-danger-500 focus:ring-danger-500' : ''}`}
            {...register('method', { required: 'İşlem türü seçimi gereklidir' })}
          >
            <option value="stok_girisi">Stok Girişi</option>
            <option value="nakit">Nakit Ödeme</option>
            <option value="kredi_karti">Kredi Kartı</option>
            <option value="banka_havalesi">Banka Havalesi</option>
          </select>
          {errors.method && (
            <p className="text-sm text-danger-600 mt-1">{errors.method.message}</p>
          )}
        </div>

        {/* Product Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ürün Seçimi * <span className="text-gray-500">({selectedProducts.length} ürün seçili)</span>
          </label>
          
          {/* Search Field */}
          <div className="relative mb-3">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Selected Products */}
          {selectedProducts.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-apple border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Seçilen Ürünler:</h4>
              <div className="space-y-2">
                {selectedProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between bg-white p-2 rounded border">
                    <span className="text-sm font-medium text-gray-900">
                      {product.name} ({product.unit})
                    </span>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        step={product.unit === 'adet' || product.unit === 'piece' || product.unit === 'parça' ? "1" : "0.01"}
                        min={product.unit === 'adet' || product.unit === 'piece' || product.unit === 'parça' ? "1" : "0.01"}
                        value={product.quantity}
                        onChange={(e) => updateProductQuantity(product.id, e.target.value)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeProduct(product.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Kaldır
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Products Grid */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-apple">
            {searchTerm ? (
              // Search mode - show filtered products without grouping
              <div className="grid grid-cols-1 divide-y divide-gray-200">
                {filteredProducts.map((globalProduct) => (
                  <button
                    key={globalProduct.id}
                    type="button"
                    onClick={() => addProduct(globalProduct)}
                    className="p-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {getProductName(globalProduct)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Birim: {globalProduct.unit || 'adet'} • {getCategoryName(globalProduct.category_id)}
                        </p>
                      </div>
                      <Plus className="h-4 w-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              // Category mode - show products grouped by category
              <div className="space-y-1">
                {groupProductsByCategory(availableProducts).map((category) => (
                  <div key={category.id} className="border-b border-gray-100 last:border-b-0">
                    {/* Category Header */}
                    <button
                      type="button"
                      onClick={() => toggleCategoryCollapse(category.id)}
                      className="w-full px-3 py-2 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-2">
                        {collapsedCategories.has(category.id) ? (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="text-sm font-medium text-gray-700">
                          {category.name}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                          {category.products.length}
                        </span>
                      </div>
                    </button>
                    
                    {/* Category Products */}
                    {!collapsedCategories.has(category.id) && (
                      <div className="divide-y divide-gray-100">
                        {category.products.map((globalProduct) => (
                          <button
                            key={globalProduct.id}
                            type="button"
                            onClick={() => addProduct(globalProduct)}
                            className="w-full p-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {getProductName(globalProduct)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Birim: {globalProduct.unit || 'adet'}
                                </p>
                              </div>
                              <Plus className="h-4 w-4 text-gray-400" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {filteredProducts.length === 0 && searchTerm && (
            <p className="text-center py-4 text-gray-500 text-sm">
              "{searchTerm}" için ürün bulunamadı
            </p>
          )}
        </div>

        {/* Note Field */}
        <div>
          <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
            Not <span className="text-gray-400">(İsteğe bağlı)</span>
          </label>
          <textarea
            id="note"
            rows={3}
            placeholder="Stok girişi ile ilgili not..."
            className="input-field resize-none"
            {...register('note')}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || selectedProducts.length === 0}
          className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Kaydediliyor...</span>
            </>
          ) : (
            <>
              <Plus size={16} />
              <span>
                {method === 'stok_girisi' ? 'Stok Girişi Ekle' : 'Ödeme Ekle'}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

export default AddStockForm; 